import { z } from 'zod'
import { oc } from '@orpc/contract'
import { comparisonSchema, duplicateReviewSummarySchema } from './patient-merge-shared.contract'

/**
 * Duplicate review queue and comparison (PD-1986). Plain fields at the boundary — never FHIR
 * resources. `resolved` in the list filter is the three finished states: not a duplicate, merged
 * and cancelled.
 */

// Same shape as `listPatients`' page request/response (ModelPageRequest / ModelPage).
export const pageRequestSchema = z.object({
  offset: z.number().int().min(0),
  count: z.number().int().min(0),
  limit: z.number().int().min(0),
})

export const DUPLICATE_REVIEW_LIST_STATUSES = ['awaiting', 'later', 'resolved'] as const
export const DUPLICATE_REVIEW_DECISIONS = ['not-duplicate', 'review-later', 'reopen'] as const

export const duplicateReviewList = oc
  .route({ method: 'POST', path: '/v2/patient-merge/reviews/list' })
  .input(
    z.object({
      page: pageRequestSchema,
      status: z.enum(DUPLICATE_REVIEW_LIST_STATUSES),
    }),
  )
  .output(
    z.object({
      page: pageRequestSchema.extend({
        size: z.number().int().min(0),
        total: z.number().int().min(0),
        truncated: z.boolean().optional(),
        incompleteTotal: z.boolean().optional(),
      }),
      data: z.array(duplicateReviewSummarySchema),
    }),
  )

// Not under `/reviews/…`: `GET /v2/patient-merge/reviews/counts` would be shadowed by
// `GET /v2/patient-merge/reviews/{reviewId}` (orpc/contracts.spec.ts forbids that).
export const duplicateReviewCounts = oc
  .route({ method: 'GET', path: '/v2/patient-merge/review-counts' })
  .output(
    z.object({
      awaiting: z.number().int().min(0),
      later: z.number().int().min(0),
      /** Patients named by an awaiting or review-later review the caller can see. */
      openPatientIds: z.array(z.string()),
    }),
  )

export const duplicateReviewOpen = oc
  .route({ method: 'POST', path: '/v2/patient-merge/reviews/open' })
  .input(
    z.object({
      patientIds: z
        .tuple([z.string().min(1), z.string().min(1)])
        .refine(([a, b]) => a !== b, { message: 'Choose two different patients' }),
    }),
  )
  .output(z.object({ reviewId: z.string() }))

export const duplicateReviewGet = oc
  .route({ method: 'GET', path: '/v2/patient-merge/reviews/{reviewId}' })
  .input(
    z.object({
      reviewId: z.string().min(1),
      /** Conflicts are computed for keeping this record; default is the recommended one. */
      keepPatientId: z.string().min(1).optional(),
    }),
  )
  .output(comparisonSchema)

export const duplicateReviewDecide = oc
  .route({ method: 'POST', path: '/v2/patient-merge/reviews/{reviewId}/decision' })
  .input(
    z.object({
      reviewId: z.string().min(1),
      decision: z.enum(DUPLICATE_REVIEW_DECISIONS),
      note: z.string().optional(),
    }),
  )
  .output(duplicateReviewSummarySchema)

export const duplicateReviewForPatient = oc
  .route({ method: 'GET', path: '/v2/patient-merge/reviews/for-patient/{patientId}' })
  .input(z.object({ patientId: z.string().min(1) }))
  .output(z.object({ reviewId: z.string().nullable() }))
